<script lang="ts">
import { defineComponent } from 'vue'
import { MomentivePrintService } from './momentive-print.service'

interface PrintResult {
  success: boolean,
  message?: string
}

export default defineComponent({
  name: 'MomentivePrint',

  data () {
    return {
      service: null as MomentivePrintService | null
    }
  },

  methods: {
    /**
     * Check whether the Momentive Print Service is reachable.
     * @returns {boolean} whether the service is available
     */
    async initialize (): Promise<boolean> {
      this.service = new MomentivePrintService(process.env.VUE_APP_MOMENTIVE_PRINT_SERVICE_URL)
      try {
        await this.service.getPrinters()
        return true
      } catch {
        return false
      }
    },

    async getPrinters (): Promise<string[]> {
      const printers = await (this.service as MomentivePrintService).getPrinters()
      return printers.map(printer => printer.name)
    },

    async getDefaultPrinter (): Promise<string | null> {
      return await (this.service as MomentivePrintService).getDefaultPrinter()
    },

    /**
     * Submit a single (already dimension-fixed) SVG document to print, then poll the
     * queue until the job has spooled (or time out) before resolving. MPS submits jobs
     * to a physical print queue, so waiting here confirms the job actually spooled
     * rather than only that the service accepted the request.
     * @param {string} fixedSvg
     * @param {string | null} printerName
     * @param {string} [documentName] participant name recorded as the print job's document name; falls back to 'Badge'
     * @returns {PrintResult}
     */
    async printOne (fixedSvg: string, printerName: string | null, documentName?: string): Promise<PrintResult> {
      const service = this.service as MomentivePrintService
      const docName = (typeof documentName === 'string' && documentName.trim()) ? documentName.trim() : 'Badge'

      let spoolerDocName: string | null = null
      try {
        const result = await service.print(fixedSvg, printerName ?? undefined, docName, 1)
        if (!result.success) {
          return { success: false, message: result.message ?? 'Print failed' }
        }
        spoolerDocName = result.documentName
      } catch (err: any) {
        return { success: false, message: err?.message ?? 'Print error' }
      }

      // Poll queue until job disappears (completed) or timeout.
      // Match on document name — it's set as PrintDocument.DocumentName on the backend
      // and surfaced by WMI as Win32_PrintJob.Document, so it's the reliable cross-system link.
      const maxPolls = 30
      for (let i = 0; i < maxPolls; i++) {
        await new Promise(resolve => setTimeout(resolve, 2000))
        try {
          const queue = await service.getQueue()
          const stillPending = queue.some(j => j.document === spoolerDocName)
          if (!stillPending) break
          if (i === maxPolls - 1) {
            return { success: false, message: 'Print job timed out' }
          }
        } catch {
          // Queue check failed — proceed optimistically
          break
        }
      }

      return { success: true }
    }
  }
})
</script>

<template>
  <div />
</template>
